import { useEffect, useRef, useState } from 'react';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CloudDownload from '@mui/icons-material/CloudDownload';
import Computer from '@mui/icons-material/Computer';
import DarkMode from '@mui/icons-material/DarkMode';
import EditDocument from '@mui/icons-material/EditDocument';
import LightMode from '@mui/icons-material/LightMode';
import ListAlt from '@mui/icons-material/ListAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import Palette from '@mui/icons-material/Palette';
import PestControl from '@mui/icons-material/PestControl';
import Science from '@mui/icons-material/Science';
import { Box, Divider, Popover, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuthContext } from 'features/auth/context/useAuthContext';
import { selectUser } from 'features/auth/store/authSelectors';
import { DevTools } from 'features/dev-tools/components/DevTools';
import { selectShowDeveloperFeatures } from 'features/dev-tools/store/devToolsSelectors';
import { selectOptionalParticipationDocData } from 'features/participations/store/participationSelectors';
import { RoutePath } from 'features/routing/constants';
import { SignOutConfirmationDialog } from 'features/settings/components/SignOutConfirmationDialog';
import { useAppSelector } from 'features/store/hooks';
import { useColorMode } from 'features/theme/useColorMode';
import { ListRow, SectionLabel } from 'features/ui';
import { BottomSheet } from './BottomSheet';
import { InitialsAvatar } from './InitialsAvatar';
import type { SvgIconComponent } from '@mui/icons-material';
import type { PopoverActions } from '@mui/material';

export type AccountMenuProps = {
  variant: 'popover' | 'sheet';
  open: boolean;
  /** Popover anchor (ignored by the sheet variant). */
  anchorEl?: HTMLElement | null;
  /** Popover direction: 'up' from a bottom anchor, 'down' from a top bar. */
  placement?: 'up' | 'down';
  onClose: () => void;
};

const rowIconSx = { fontSize: 20, color: 'text.secondary' } as const;
const chevronSx = { fontSize: 16, color: 'text.tertiary' } as const;

type DevEntry = {
  id: string;
  labelKey: string;
  Icon: SvgIconComponent;
  to: string;
};

const AccountMenuContent = ({
  variant,
  onClose,
}: {
  variant: 'popover' | 'sheet';
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const participation = useAppSelector(selectOptionalParticipationDocData);
  const showDeveloperFeatures = useAppSelector(selectShowDeveloperFeatures);
  const authContext = useAuthContext();
  const { setColorMode, darkModeAllowed } = useColorMode();
  const isDark = theme.palette.mode === 'dark';

  const [signOutOpen, setSignOutOpen] = useState(false);

  const goTo = (to: string) => {
    onClose();
    void navigate(to);
  };

  const devEntries: DevEntry[] = [
    ...(user.role === 'admin'
      ? [
          {
            id: 'ProtocolEditor',
            labelKey: 'ProtocolEditor',
            Icon: EditDocument,
            to: participation
              ? RoutePath.ProtocolEdit(participation.protocolId)
              : RoutePath.ProtocolEditWithoutId,
          },
          {
            id: 'ProtocolList',
            labelKey: 'ProtocolList',
            Icon: ListAlt,
            to: RoutePath.ProtocolList,
          },
          {
            id: 'Brands',
            labelKey: 'Brands',
            Icon: Palette,
            to: RoutePath.Brands,
          },
        ]
      : []),
    ...(showDeveloperFeatures
      ? [
          {
            id: 'SystemInfo',
            labelKey: 'SystemInfo',
            Icon: Computer,
            to: participation
              ? RoutePath.SystemInfoWithLayout(participation.id)
              : RoutePath.SystemInfoStandalone,
          },
          {
            id: 'DevDebug',
            labelKey: 'DevDebug',
            Icon: PestControl,
            to: participation
              ? RoutePath.DevDebugWithLayout(participation.id)
              : RoutePath.DevDebugStandalone,
          },
          {
            id: 'DevDataExport',
            labelKey: 'DevDataExport',
            Icon: CloudDownload,
            to: participation
              ? RoutePath.DevDataExportWithLayout(participation.id)
              : RoutePath.DevDataExportStandalone,
          },
          {
            id: 'DevDemo',
            labelKey: 'DevDemo',
            Icon: Science,
            to: RoutePath.DevDemo,
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ p: variant === 'sheet' ? '4px 6px 10px' : '4px 6px' }}>
      {/* Profile header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1 }}>
        <InitialsAvatar />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
            {user.displayName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
            {user.email}
          </Typography>
        </Box>
        {/* Self-gates on PUBLIC_ALLOW_DEVELOPER_FEATURES */}
        <Box sx={{ alignSelf: 'flex-start', flexShrink: 0 }}>
          <DevTools />
        </Box>
      </Box>
      {darkModeAllowed && (
        <>
          <Divider sx={{ my: 0.75 }} />
          <ListRow
            leading={
              isDark ? (
                <DarkMode sx={rowIconSx} />
              ) : (
                <LightMode sx={rowIconSx} />
              )
            }
            title={t('ShellDarkModeLabel')}
            trailing={
              <Switch
                checked={isDark}
                onChange={(_event, checked) =>
                  setColorMode(checked ? 'dark' : 'light')
                }
                slotProps={{
                  input: { 'aria-label': t('ShellDarkModeLabel') },
                }}
              />
            }
            sx={{ px: 1.25 }}
          />
        </>
      )}

      {devEntries.length > 0 && (
        <>
          <Divider sx={{ my: 0.75 }} />
          <Box sx={{ px: 1.25, pt: 0.5 }}>
            <SectionLabel>{t('ShellAccountDevSectionLabel')}</SectionLabel>
          </Box>
          {devEntries.map(entry => (
            <ListRow
              key={entry.id}
              leading={<entry.Icon sx={rowIconSx} />}
              title={t(entry.labelKey)}
              trailing={<ChevronRight sx={chevronSx} />}
              onClick={() => goTo(entry.to)}
              sx={{ px: 1.25 }}
            />
          ))}
        </>
      )}

      <Divider sx={{ my: 0.75 }} />
      <ListRow
        leading={<LogoutIcon sx={{ fontSize: 20, color: 'error.main' }} />}
        title={t('LogOut')}
        onClick={() => setSignOutOpen(true)}
        sx={{ px: 1.25, color: 'error.main' }}
      />

      <SignOutConfirmationDialog
        open={signOutOpen}
        handleClose={() => setSignOutOpen(false)}
        handleConfirm={async () => {
          setSignOutOpen(false);
          onClose();
          await authContext.logout();
        }}
      />
    </Box>
  );
};

/** The avatar/profile account menu: popover (desktop) or bottom sheet (mobile). */
export const AccountMenu = ({
  variant,
  open,
  anchorEl,
  placement = 'up',
  onClose,
}: AccountMenuProps) => {
  const popoverActions = useRef<PopoverActions>(null);
  const showDeveloperFeatures = useAppSelector(selectShowDeveloperFeatures);
  useEffect(() => {
    // The dev toggle grows/shrinks the menu while open — re-anchor so the
    // popover doesn't extend past the viewport
    popoverActions.current?.updatePosition();
  }, [showDeveloperFeatures]);

  if (variant === 'sheet') {
    return (
      <BottomSheet open={open} onClose={onClose}>
        <AccountMenuContent variant="sheet" onClose={onClose} />
      </BottomSheet>
    );
  }

  const up = placement === 'up';
  return (
    <Popover
      action={popoverActions}
      open={open && anchorEl != null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: up ? 'top' : 'bottom',
        horizontal: up ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: up ? 'bottom' : 'top',
        horizontal: up ? 'left' : 'right',
      }}
      elevation={0}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: 280,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: up ? -1.5 : 1.5,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              [up ? 'bottom' : 'top']: 0,
              [up ? 'left' : 'right']: 24,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: `translateY(${up ? '50%' : '-50%'}) rotate(45deg)`,
              zIndex: 0,
            },
          },
        },
      }}
    >
      <AccountMenuContent variant="popover" onClose={onClose} />
    </Popover>
  );
};
